import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Divider, Grid, IconButton, Typography, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Modal from "./Modal";
import ProductCard from "./ProductCard";
import { transformDataForTrayModal } from "utils/transformdata";
import { addToCart, removeFromCart, clearCart } from "store/cartSlice";
import { useRemoveDesings } from "features/set/hooks/useRemoveDesigns";
import RemoveModal from "./RemoveModal/RemoveModal";
import { useGetDesigns } from "features/set/api/get-designs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import catalogAPIs from "features/catalogue/api/catalogAPIs";
import { toast as myToast } from "react-toastify";
import { useGlobalContext } from "context/GlobalContext";
import { getIcon } from "utils/iconsConfig";

const MODAL_WIDTH_PX = 515;
const CARD_INNER_WIDTH_PX = 473;

const cardStyle = {
  backgroundColor: "#1a1a1a",
  color: "#EDEDED",
  boxShadow: "none",
  padding: "14px 16px",
  borderRadius: "12px",
  border: "1px solid #2f2f2f",
};

function filterDesignsNotInCatalog(
  designs,
  {
    styleNumbersInSetRow = [],
    updateProductsData = [],
    catalogProductsPresence = { styleNumbers: [], designIds: [] },
    updateCatalogProductsDataFromContext = [],
  }
) {
  const styleInCatalog = new Set([
    ...styleNumbersInSetRow.filter(Boolean),
    ...updateProductsData.map((p) => p.styleNumber).filter(Boolean),
    ...(catalogProductsPresence.styleNumbers || []).filter(Boolean),
    ...updateCatalogProductsDataFromContext.map((p) => p.styleNumber).filter(Boolean),
  ]);

  const idInCatalog = new Set([
    ...(catalogProductsPresence.designIds || []).map(String),
    ...updateProductsData.map((p) => p._id || p.id).filter(Boolean).map(String),
    ...updateCatalogProductsDataFromContext.map((p) => p._id || p.id).filter(Boolean).map(String),
  ]);

  return (designs || []).filter((d) => {
    const sn = d.styleNumber;
    if (!sn) return false;
    if (styleInCatalog.has(sn)) return false;
    const id = d._id || d.id;
    if (id && idInCatalog.has(String(id))) return false;
    return true;
  });
}

function IncompleteSetModal({
  open,
  onClose,
  setId,
  designsInCatalogForSet = [],
  catalogId,
  catalogueName,
  catalogProductsPresence,
}) {
  const location = useLocation();
  const dispatch = useDispatch();
  const cartData = useSelector((state) => state.cart.cartData);
  const { updateCatalogProductsData } = useGlobalContext();

  const [btnTitles, setBtnTitles] = useState({});
  const [presentInCatalog, setPresentInCatalog] = useState({});
  const [removeDesignModal, setRemoveDesignModal] = useState(false);
  const [modalBtnInfo, setModalBtnInfo] = React.useState([]);
  const { handleRemoveDesign } = useRemoveDesings();
  const queryClient = useQueryClient();

  const { mutate: updateCatalog, isPending: isUpdatingCatalog } = useMutation({
    mutationFn: catalogAPIs.editCatalog,
  });

  const isInCart = (styleNumber) => cartData.some((item) => item.styleNumber === styleNumber);

  const stylesInCatalogRow = useMemo(
    () => (designsInCatalogForSet || []).map((d) => d.styleNumber),
    [designsInCatalogForSet]
  );

  const { data: setQueryData, isLoading, isError, error } = useGetDesigns({
    setID: open && setId ? setId : "",
    sortBy: "Design Date",
    sortOrder: "asc",
    searchTerm: "",
  });

  const visibleDesigns = useMemo(() => {
    const raw = setQueryData?.designs ?? [];
    const updateFromSession = JSON.parse(sessionStorage.getItem("updateProductsData")) || [];
    return filterDesignsNotInCatalog(raw, {
      styleNumbersInSetRow: stylesInCatalogRow,
      updateProductsData: updateFromSession,
      catalogProductsPresence: catalogProductsPresence || { styleNumbers: [], designIds: [] },
      updateCatalogProductsDataFromContext: updateCatalogProductsData || [],
    });
  }, [
    setQueryData,
    stylesInCatalogRow,
    cartData,
    open,
    catalogProductsPresence,
    updateCatalogProductsData,
  ]);

  useEffect(() => {
    if (!open) return;
    return () => {
      dispatch(clearCart());
    };
  }, [open, dispatch]);

  const openRemoveModal = useCallback(
    (designId, setIdForRemove) => {
      const dynamicBtnInfo = [
        {
          btnName: "Remove",
          bgColor: "#E77B7B",
          click: () => {
            handleRemoveDesign(designId, setIdForRemove);
            setRemoveDesignModal(false);
          },
        },
        {
          btnName: "Keep",
          bgColor: "#50C878",
          click: () => setRemoveDesignModal(false),
        },
      ];
      setModalBtnInfo(dynamicBtnInfo);
      setRemoveDesignModal(true);
    },
    [handleRemoveDesign]
  );

  const addDesignToCart = (rawDesign) => {
    dispatch(
      addToCart({
        product: { ...rawDesign, isAddedInCart: true },
        location,
      })
    );
    setBtnTitles((prev) => ({
      ...prev,
      [rawDesign._id]: "Added",
    }));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const removeDesignFromCart = (rawDesign) => {
    setBtnTitles((prev) => ({
      ...prev,
      [rawDesign._id]: "Add",
    }));
    dispatch(removeFromCart(rawDesign));
    sessionStorage.setItem("RecentlyRemoved", JSON.stringify(rawDesign));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  useEffect(() => {
    const updateCatalogProductsData = JSON.parse(sessionStorage.getItem("updateProductsData")) || [];
    visibleDesigns.forEach((item) => {
      const isAddedToCart = isInCart(item.styleNumber);
      const isAddedToCatalog = updateCatalogProductsData.some((c) => c.styleNumber === item.styleNumber);

      setBtnTitles((prev) => ({
        ...prev,
        [item._id]: isAddedToCart || isAddedToCatalog ? "Added" : "Add",
      }));

      setPresentInCatalog((prev) => ({
        ...prev,
        [item._id]: isAddedToCatalog,
      }));
    });
  }, [visibleDesigns, open, cartData]);

  const addDesignIdsFromCart = useMemo(() => {
    const cartStyleNumbers = new Set((cartData || []).map((c) => c.styleNumber).filter(Boolean));
    const ids = visibleDesigns
      .filter((d) => d.styleNumber && cartStyleNumbers.has(d.styleNumber))
      .map((d) => d._id)
      .filter(Boolean);
    return [...new Set(ids)];
  }, [cartData, visibleDesigns]);

  const hasCartItems = (cartData?.length ?? 0) > 0;
  const canSubmitAddToCatalog =
    Boolean(catalogId && setId && visibleDesigns.length > 0 && hasCartItems && addDesignIdsFromCart.length > 0) &&
    !isLoading &&
    !isUpdatingCatalog;

  const handleAddAllToCatalog = () => {
    if (!catalogId || !hasCartItems || addDesignIdsFromCart.length === 0) return;

    updateCatalog(
      {
        addDesigns: addDesignIdsFromCart,
        removeDesigns: [],
        catalogId,
        catalogName: catalogueName || "Catalog",
        isArchive: false,
        id: catalogId,
        state: location.state,
      },
      {
        onSuccess: (data) => {
          if (data?.data?.status === 200) {
            onClose();
            myToast.success(data?.data?.message || "Catalog updated successfully!", {
              position: "top-right",
              autoClose: 2000,
            });
            void queryClient.invalidateQueries({ queryKey: ["catalogProducts"] });
            queryClient.removeQueries({ queryKey: ["catalogCountData"] });
          } else {
            myToast.error(`Error: ${data?.data?.message || "An unexpected error occurred"}`, {
              position: "top-right",
              autoClose: 2000,
            });
          }
        },
        onError: (error) => {
          myToast.error(
            `Error: ${error?.response?.data?.message || error.message || "An unexpected error occurred"}`,
            {
              position: "top-right",
              autoClose: 2000,
            }
          );
        },
      }
    );
  };

  const showCartButtons = true;

  return (
    <Modal
      open={open}
      handleClose={onClose}
      style={{
        width: `${MODAL_WIDTH_PX}px`,
        height: "100vh",
        maxHeight: "100vh",
        mb: 0,
        bgcolor: "#fff",
        padding: "12px 20px 16px 20px",
        scroll: "hidden",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "center", paddingTop: "8px", paddingBottom: "4px" }}>
            <Box sx={{ width: 48, height: 5, borderRadius: 5, backgroundColor: "#2a2a2a" }} />
          </Box>

          <Grid container spacing={2} sx={{ alignItems: "center", padding: "10px 0 0 0" }}>
            <Grid item xs={10}>
              <Typography
                component="h2"
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "24px",
                  lineHeight: 1,
                  letterSpacing: 0,
                  margin: 0,
                  color: "#000000",
                }}
              >
                Incomplete Set
              </Typography>
            </Grid>
            <Grid item xs={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ color: "#000" }}>
              {getIcon("Cross", "black")}
              </IconButton>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            mt: 2,
            pb: 3,
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none",
              width: 0,
              height: 0,
            },
          }}
        >
          {!setId && open && (
            <Typography sx={{ color: "#666", fontFamily: "Poppins", padding: "24px 0", textAlign: "center" }}>
              Missing set reference — cannot load designs.
            </Typography>
          )}

          {setId && isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {setId && isError && (
            <Typography sx={{ color: "#c00", fontFamily: "Poppins", padding: "16px 0", textAlign: "center" }}>
              {error?.message || "Could not load set designs."}
            </Typography>
          )}

          {setId && !isLoading && !isError && visibleDesigns.length === 0 && (
            <Typography sx={{ color: "#444", fontFamily: "Poppins", padding: "24px 0", textAlign: "center" }}>
              All designs from this set are already in this catalog.
            </Typography>
          )}

          {setId &&
            !isLoading &&
            !isError &&
            visibleDesigns.map((rawDesign, index) => {
              const displayData = transformDataForTrayModal(rawDesign);
              const selectedCartProducts = { ...rawDesign, isAddedInCart: true };

              return (
                <Box key={rawDesign._id} width="100%">
                  <Box
                    className="product"
                    sx={{
                      padding: "16px 0",
                      "& .content-typo": { color: "#EDEDED !important" },
                      "& .MuiTypography-root": { color: "#EDEDED" },
                    }}
                  >
                    <ProductCard
                      imageModalAnchor="left"
                      data={displayData}
                      style={{
                        maxWidth: `${CARD_INNER_WIDTH_PX}px`,
                        minWidth: `${CARD_INNER_WIDTH_PX}px`,
                        minHeight: "254px",
                        height: "auto",
                      }}
                      addtoCart={() => addDesignToCart(rawDesign)}
                      removeCart={
                        showCartButtons
                          ? () => removeDesignFromCart(rawDesign)
                          : () => openRemoveModal(setId, rawDesign._id)
                      }
                      selectedCartProducts={selectedCartProducts}
                      cardStyle={cardStyle}
                      isBtn={showCartButtons}
                      btnTilte={
                        showCartButtons ? (btnTitles[rawDesign._id] === "Added" ? "Added" : "Add") : "Remove"
                      }
                      presentInCatalog={presentInCatalog[rawDesign._id] === true}
                    
                      allData={visibleDesigns}
                      updateCatagoryData={cartData}
                    />
                  </Box>
                </Box>
              );
            })}
        </Box>

        <Box sx={{ flexShrink: 0, paddingTop: "16px", paddingBottom: "8px" }}>
          <button
            type="button"
            onClick={handleAddAllToCatalog}
            disabled={!canSubmitAddToCatalog}
            style={{
              width: "100%",
              padding: "14px 16px",
              borderRadius: "5px",
              border: "none",
              cursor: canSubmitAddToCatalog ? "pointer" : "not-allowed",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "18px",
              lineHeight: 1,
              letterSpacing: 0,
              backgroundColor: canSubmitAddToCatalog ? "#50C878" : "#444444",
              color: "#000000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            {isUpdatingCatalog && <CircularProgress size={20} sx={{ color: "#000000" }} />}
            Add Designs to Catalog
          </button>
        </Box>
      </Box>

      <RemoveModal open={removeDesignModal} setOpen={setRemoveDesignModal} btnInfo={modalBtnInfo} heading="Do you want to Remove the Design?" />
    </Modal>
  );
}

export default IncompleteSetModal;
